import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * HC Packaging Plus design table (quote mode).
 *
 * One row per design: name, artwork file and quantity. Add to cart is intercepted for the
 * linked product form: every row is validated, then each row is added as its own cart line,
 * one request per row so every file uploads on its own. Shopify stores the file and the line
 * gets "Design name" and "Artwork file" properties (the file's URL, plus the customer's file name
 * as the hidden "_Original file name"). A file over the size limit switches its row to a link
 * box, which is sent as "Artwork link" instead.
 *
 * Shopify's CDN re-encodes image uploads (a PNG downloads as WebP or JPEG), so image types
 * listed in data-zip-extensions are sent inside an uncompressed .zip and arrive byte for byte.
 *
 * The product form's (hidden) quantity input stays the single source of the minimum: its
 * `min` attribute already reflects custom.moq, the category minimum and Shopify's own rule.
 */
const LINK_PATTERN = /^https?:\/\/\S+$/i;

/** @type {Uint32Array | undefined} */
let crcTable;

/** @param {Uint8Array} bytes */
function crc32(bytes) {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
      let c = n;
      for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * A name Windows Explorer can extract: Mac and phone file names may hold characters Windows refuses.
 * @param {string} name
 */
function safeEntryName(name) {
  let safe = name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').replace(/[. ]+$/, '') || 'artwork';
  if (/^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\.|$)/i.test(safe)) safe = `_${safe}`;
  return safe;
}

/**
 * Wraps one file in an uncompressed .zip, so Shopify's image CDN serves it byte for byte.
 * @param {string} name
 * @param {Uint8Array} bytes
 * @param {number} lastModified
 * @returns {Blob}
 */
function zipOne(name, bytes, lastModified) {
  const nameBytes = new TextEncoder().encode(safeEntryName(name));
  const crc = crc32(bytes);
  const date = new Date(lastModified || Date.now());
  const year = Math.min(Math.max(date.getFullYear(), 1980), 2107);
  const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate = ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();

  const local = new DataView(new ArrayBuffer(30));
  local.setUint32(0, 0x04034b50, true);
  local.setUint16(4, 20, true);
  local.setUint16(6, 0x0800, true); // UTF-8 file name
  local.setUint16(8, 0, true); // stored, no compression
  local.setUint16(10, dosTime, true);
  local.setUint16(12, dosDate, true);
  local.setUint32(14, crc, true);
  local.setUint32(18, bytes.length, true);
  local.setUint32(22, bytes.length, true);
  local.setUint16(26, nameBytes.length, true);
  local.setUint16(28, 0, true);

  const central = new DataView(new ArrayBuffer(46));
  central.setUint32(0, 0x02014b50, true);
  central.setUint16(4, 20, true);
  central.setUint16(6, 20, true);
  central.setUint16(8, 0x0800, true);
  central.setUint16(10, 0, true);
  central.setUint16(12, dosTime, true);
  central.setUint16(14, dosDate, true);
  central.setUint32(16, crc, true);
  central.setUint32(20, bytes.length, true);
  central.setUint32(24, bytes.length, true);
  central.setUint16(28, nameBytes.length, true);
  // Extra field, comment, disk number, attributes and the local header offset (0) stay zero.

  const centralSize = 46 + nameBytes.length;
  const centralOffset = 30 + nameBytes.length + bytes.length;
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, 1, true);
  end.setUint16(10, 1, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, centralOffset, true);

  return new Blob([local, nameBytes, bytes, central, nameBytes, end], { type: 'application/zip' });
}

/**
 * @typedef {{name: string, file: File | null, link: string, linkMode: boolean, qty: number, empty: boolean}} RowData
 */

class HcDesignTable extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {AbortController | undefined} */
  #requestController;
  /** @type {WeakMap<HTMLElement, File>} */
  #files = new WeakMap();
  #rowCounter = 1;
  #busy = false;
  /** @type {any} */
  #pendingSelect = null;
  #selectGeneration = 0;
  /** True after the latest variant change failed: the form still holds the previous variant's id. */
  #variantStale = false;
  /** Set while Add to cart is being pressed, so leaving the quantity box does not shift the button away. */
  #pressingAdd = false;
  #pressTimer = 0;
  /** @type {HTMLElement | null} The row whose quantity check was put off by that press. */
  #pressedQtyRow = null;
  /** Asks before leaving the page while designs are still uploading. @param {BeforeUnloadEvent} event */
  #leaveGuard = (event) => {
    event.preventDefault();
    // @ts-ignore Older browsers prompt only when returnValue is set to something truthy.
    event.returnValue = true;
  };

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('dragover', this.#onDragOver, { signal });
    this.addEventListener('dragleave', this.#onDragLeave, { signal });
    this.addEventListener('drop', this.#onDrop, { signal });
    // A file dropped just outside the table would otherwise be opened by the browser, leaving the page.
    window.addEventListener('dragover', this.#onWindowDrag, { signal });
    window.addEventListener('drop', this.#onWindowDrag, { signal });
    // The theme routes on:click / on:submit through capture listeners on document (component.js).
    // Window capture runs before those, so the table decides first and can stop the default add.
    window.addEventListener('pointerdown', this.#onDocumentPointerDown, { capture: true, signal });
    window.addEventListener('click', this.#onDocumentClick, { capture: true, signal });
    window.addEventListener('submit', this.#onDocumentSubmit, { capture: true, signal });
    // Horizon sets input[name="id"] only after the variant's section fetch resolves.
    this.closest('.shopify-section')?.addEventListener(
      'shopify:product:select',
      (event) => {
        // Product cards in the same section (recommendations) select their own products.
        const card = event.target instanceof Element ? event.target.closest('product-card') : null;
        if (card && !card.contains(this)) return;
        const promise = /** @type {any} */ (event).promise ?? null;
        const generation = ++this.#selectGeneration;
        this.#pendingSelect = promise;
        // The theme updates the quantity minimum from the new variant; keep the "Min. [min]" placeholders in step.
        promise?.then(
          () => {
            if (generation === this.#selectGeneration) {
              this.#variantStale = false;
              // The option loaded this time: drop the earlier "could not load" note.
              const summary = this.querySelector('[data-form-error]');
              if (summary && summary.textContent === this.#message('msgVariantFailed')) this.#setFormError('');
            }
            setTimeout(() => {
              this.#refreshRows();
              // An error already on show should name the new variant's minimum.
              if (!this.#busy) for (const row of this.#rows) this.#revalidate(row);
            });
          },
          (/** @type {any} */ error) => {
            // An abort means a newer choice replaced this one; any other failure left the old variant id.
            if (generation === this.#selectGeneration && error?.name !== 'AbortError') this.#variantStale = true;
          }
        );
      },
      { signal }
    );

    this.#rowCounter = this.#rows.length;
    this.#refreshRows();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#requestController?.abort();
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    return form instanceof HTMLFormElement ? form : null;
  }

  /** @returns {HTMLElement[]} */
  get #rows() {
    return Array.from(this.querySelectorAll('[data-row]')).filter((row) => row instanceof HTMLElement);
  }

  /** @returns {number} The product's minimum, read from the product form's quantity input. */
  get #min() {
    const input = this.#form?.querySelector('input[name="quantity"]');
    const min = input instanceof HTMLInputElement ? parseInt(input.min, 10) : NaN;
    return Number.isFinite(min) && min > 0 ? min : 1;
  }

  /** @returns {number} Largest file Shopify will take, in MB. */
  get #maxMb() {
    const max = parseFloat(this.dataset.maxMb || '');
    return Number.isFinite(max) && max > 0 ? max : 20;
  }

  /** @returns {string[]} Allowed extensions, lower case, without the dot. */
  get #extensions() {
    return (this.dataset.extensions || 'png,psd,ai,pdf')
      .split(',')
      .map((ext) => ext.trim().replace(/^\./, '').toLowerCase())
      .filter(Boolean);
  }

  /** @returns {string[]} Extensions sent inside a .zip, lower case, without the dot. */
  get #zipExtensions() {
    return (this.dataset.zipExtensions ?? 'png,psd')
      .split(',')
      .map((ext) => ext.trim().replace(/^\./, '').toLowerCase())
      .filter(Boolean);
  }

  /**
   * The bytes to send for a row's file. Reading the file into memory first also avoids the
   * Safari 26.5-26.7 bug that sends an empty body for a File taken straight from an input.
   * @param {File} file
   * @returns {Promise<{blob: Blob, name: string, zipped: boolean}>}
   */
  async #uploadBody(file) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (this.#zipExtensions.includes(this.#extension(file.name))) {
      return { blob: zipOne(file.name, bytes, file.lastModified), name: `${file.name}.zip`, zipped: true };
    }
    return { blob: new Blob([bytes], { type: file.type || 'application/octet-stream' }), name: file.name, zipped: false };
  }

  /**
   * @param {string} key
   * @param {Object} [values]
   */
  #message(key, values = {}) {
    let text = this.dataset[key] || '';
    for (const [token, value] of Object.entries(values)) text = text.replaceAll(`[${token}]`, String(value));
    return text;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (this.#busy) {
      if (event.target.closest('[data-add], [data-remove], [data-file-clear], [data-file-button]')) event.preventDefault();
      return;
    }

    if (event.target.closest('[data-add]')) {
      event.preventDefault();
      this.#addRow();
      return;
    }

    const removeButton = event.target.closest('[data-remove]');
    if (removeButton) {
      event.preventDefault();
      const row = removeButton.closest('[data-row]');
      if (row instanceof HTMLElement) this.#removeRow(row);
      return;
    }

    const clearButton = event.target.closest('[data-file-clear]');
    if (clearButton) {
      event.preventDefault();
      const row = clearButton.closest('[data-row]');
      if (!(row instanceof HTMLElement)) return;
      this.#files.delete(row);
      this.#renderFile(row);
      this.#revalidate(row);
      const input = row.querySelector('[data-field="file"]');
      if (input instanceof HTMLElement) input.focus();
    }
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type === 'file') return;

    if (input.dataset.field === 'qty') {
      const digits = input.value.replace(/\D/g, '');
      if (digits !== input.value) input.value = digits;
    }

    this.#setStatus('');
    const row = input.closest('[data-row]');
    if (row instanceof HTMLElement) this.#revalidate(row);
  };

  /**
   * Flags a quantity below the minimum as soon as the customer leaves the box, not only on Add to cart.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.field !== 'qty' || this.#busy) return;
    const row = input.closest('[data-row]');
    if (!(row instanceof HTMLElement)) return;
    // Add to cart checks every row itself; showing the error now would move the button before the click lands.
    if (this.#pressingAdd) {
      this.#pressedQtyRow = row;
      return;
    }
    this.#checkQty(row);
  };

  /**
   * Shows (or clears) only the quantity problem on a row the customer is still filling in.
   * A row already flagged by Add to cart keeps its full check through #revalidate.
   * @param {HTMLElement} row
   */
  #checkQty(row) {
    const input = row.querySelector('[data-field="qty"]');
    const error = row.querySelector('[data-row-error]');
    if (!(input instanceof HTMLInputElement) || !error) return;
    const flaggedByAdd = row.dataset.qtyOnly !== 'true' && Boolean(error.textContent);
    if (flaggedByAdd) {
      this.#revalidate(row);
      return;
    }

    const text = input.value.trim();
    const tooLow = text !== '' && (parseInt(text, 10) || 0) < this.#min;
    input.setAttribute('aria-invalid', String(tooLow));
    error.textContent = tooLow ? this.#message('msgQty', { min: this.#min }) : '';
    if (tooLow) {
      row.dataset.qtyOnly = 'true';
    } else {
      delete row.dataset.qtyOnly;
      this.#clearFixSummary();
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'file') return;
    const row = input.closest('[data-row]');
    const files = Array.from(input.files ?? []);
    // Clear the picker so choosing the same file again still fires a change.
    input.value = '';
    if (this.#busy || !(row instanceof HTMLElement) || files.length === 0) return;
    this.#placeFiles(files, row);
  };

  /** @param {DragEvent} event */
  #onDragOver = (event) => {
    if (this.#busy || !event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    const row = event.target instanceof Element ? event.target.closest('[data-row]') : null;
    for (const r of this.#rows) r.classList.toggle('is-drop-target', r === row);
    this.classList.toggle('is-dragging', true);
  };

  /** @param {DragEvent} event */
  #onDragLeave = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#clearDropState();
  };

  /** @param {DragEvent} event */
  #onDrop = (event) => {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    this.#clearDropState();
    if (this.#busy) return;
    const files = Array.from(event.dataTransfer.files ?? []);
    if (files.length === 0) return;
    const row = event.target instanceof Element ? event.target.closest('[data-row]') : null;
    const start = row instanceof HTMLElement ? row : this.#rows.find((r) => !this.#files.has(r) && !this.#isLinkMode(r));
    this.#placeFiles(files, start ?? null);
  };

  /** @param {DragEvent} event */
  #onWindowDrag = (event) => {
    if (event.defaultPrevented || !event.dataTransfer?.types.includes('Files')) return;
    if (event.target instanceof HTMLInputElement && event.target.type === 'file') return;
    event.preventDefault();
    if (event.type === 'dragover') event.dataTransfer.dropEffect = 'none';
  };

  #clearDropState() {
    this.classList.remove('is-dragging');
    for (const r of this.#rows) r.classList.remove('is-drop-target');
  }

  /**
   * Puts the first file in `row` and each further file in the next row without a file,
   * adding rows as needed. Files of the wrong type are skipped with a message.
   * @param {File[]} files
   * @param {HTMLElement | null} row
   */
  #placeFiles(files, row) {
    const allowed = this.#extensions;
    const rejected = files.filter((file) => !allowed.includes(this.#extension(file.name)));
    // A 0-byte file is never usable artwork (and its chip would read "1 KB").
    const empty = files.filter((file) => !rejected.includes(file) && file.size === 0);
    const accepted = files.filter((file) => !rejected.includes(file) && !empty.includes(file));

    let target = row;
    /** @type {HTMLElement | null} */
    let firstFilled = null;
    /** @type {HTMLElement | null} */
    let last = null;
    let dropped = 0;
    let placed = 0;
    for (const file of accepted) {
      if (!target) target = this.#nextFreeRow(last);
      if (!target) {
        dropped += 1;
        continue;
      }
      this.#setFile(target, file);
      placed += 1;
      firstFilled ??= target;
      last = target;
      target = null;
    }

    // Shown once, in the summary (role="alert"); a row error here would also trip #revalidate later.
    const notes = rejected.map((file) => this.#message('msgType', { file: file.name }));
    for (const file of empty) notes.push(this.#message('msgEmpty', { file: file.name }) || this.#message('msgRead', { file: file.name }));
    if (dropped) notes.push(this.#message('msgMaxRows', { count: dropped }));
    // Keep the summary while other rows are still flagged.
    this.#setFormError(
      notes.length
        ? notes.join('\n')
        : this.#rows.some((r) => r.dataset.qtyOnly !== 'true' && r.querySelector('[aria-invalid="true"]'))
          ? this.#message('msgFix')
          : ''
    );
    // Several files fill rows further down, which may be off screen on a phone.
    this.#setStatus(placed > 1 ? this.#message('msgPlaced', { count: placed }) : '');

    // Move on to whatever the customer still has to fill in for the first new design.
    if (firstFilled) {
      const next = /** @type {HTMLInputElement | null} */ (
        firstFilled.querySelector(this.#isLinkMode(firstFilled) ? '[data-field="link"]' : '[data-field="qty"]')
      );
      next?.focus();
    }
  }

  /**
   * @param {HTMLElement | null} [after] - Only rows below this one count, so files keep their order.
   * @returns {HTMLElement | null} The next empty row (no file, no link), or a new row.
   */
  #nextFreeRow(after = null) {
    const rows = this.#rows;
    const start = after ? rows.indexOf(after) + 1 : 0;
    const free = rows.slice(start).find((r) => !this.#files.has(r) && !this.#isLinkMode(r) && this.#readRow(r).empty);
    return free ?? this.#addRow(false);
  }

  /** @param {string} fileName */
  #extension(fileName) {
    const dot = fileName.lastIndexOf('.');
    return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : '';
  }

  /**
   * @param {HTMLElement} row
   * @param {File} file
   */
  #setFile(row, file) {
    const nameInput = row.querySelector('[data-field="name"]');
    if (nameInput instanceof HTMLInputElement && (!nameInput.value.trim() || nameInput.value === row.dataset.autoName)) {
      const dot = file.name.lastIndexOf('.');
      nameInput.value = (dot > 0 ? file.name.slice(0, dot) : file.name).slice(0, 80);
      row.dataset.autoName = nameInput.value;
    }

    const error = row.querySelector('[data-row-error]');
    if (this.#uploadSize(file) > this.#maxMb * 1024 * 1024) {
      // Too big for Shopify: this row takes a link instead.
      this.#files.delete(row);
      this.#setLinkMode(row, true, file.name);
      if (error) error.textContent = '';
    } else {
      this.#files.set(row, file);
      this.#setLinkMode(row, false);
    }
    this.#renderFile(row);
    this.#revalidate(row);
  }

  /**
   * Bytes that will be uploaded for a file: zipped types carry a 98-byte zip wrapper plus the name twice.
   * @param {File} file
   */
  #uploadSize(file) {
    if (!this.#zipExtensions.includes(this.#extension(file.name))) return file.size;
    return file.size + 98 + 2 * new TextEncoder().encode(file.name).length;
  }

  /** @param {HTMLElement} row */
  #isLinkMode(row) {
    return row.dataset.linkMode === 'true';
  }

  /**
   * @param {HTMLElement} row
   * @param {boolean} on
   * @param {string} [fileName]
   * @param {string} [messageKey] - 'msgServerTooBig' when the store refused the upload.
   */
  #setLinkMode(row, on, fileName = '', messageKey = 'msgTooBig') {
    row.dataset.linkMode = String(on);
    const link = row.querySelector('[data-field="link"]');
    const note = row.querySelector('[data-file-note]');
    if (link instanceof HTMLInputElement) {
      link.hidden = !on;
      if (!on) {
        link.value = '';
        link.removeAttribute('aria-invalid');
      }
    }
    const key = this.dataset[messageKey] ? messageKey : 'msgTooBig';
    if (note) note.textContent = on ? this.#message(key, { file: fileName, max: this.#maxMb }) : '';
  }

  /** Shows the chosen file (name and size) or the upload button. @param {HTMLElement} row */
  #renderFile(row) {
    const file = this.#files.get(row);
    const chip = row.querySelector('[data-file-chip]');
    const name = row.querySelector('[data-file-name]');
    const size = row.querySelector('[data-file-size]');
    const button = row.querySelector('[data-file-button]');
    const input = row.querySelector('[data-field="file"]');

    const nameEnd = row.querySelector('[data-file-name-end]');
    if (chip instanceof HTMLElement) {
      chip.hidden = !file;
      chip.title = file ? file.name : '';
    }
    if (name instanceof HTMLElement) {
      // Ellipsis in the middle, so "...-v6.pdf" and "...-v7.pdf" still look different on a phone.
      const full = file ? file.name : '';
      const chars =
        typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
          ? Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(full), (part) => part.segment)
          : Array.from(full);
      const split = nameEnd instanceof HTMLElement && chars.length > 24 ? chars.length - 10 : chars.length;
      name.textContent = chars.slice(0, split).join('');
      if (nameEnd instanceof HTMLElement) nameEnd.textContent = chars.slice(split).join('');
    }
    if (size) size.textContent = file ? this.#formatSize(file.size) : '';
    if (button instanceof HTMLElement) button.hidden = Boolean(file);
    // The picker leaves the page (and the accessibility tree, where it would still say "No file chosen")
    // while the chip shows the chosen file.
    if (input instanceof HTMLInputElement) input.hidden = Boolean(file);
    const clear = row.querySelector('[data-file-clear]');
    if (clear instanceof HTMLElement) {
      clear.dataset.baseLabel ??= clear.getAttribute('aria-label') || 'Remove file';
      clear.setAttribute('aria-label', file ? `${clear.dataset.baseLabel} ${file.name}` : clear.dataset.baseLabel);
    }
  }

  /** @param {number} bytes */
  #formatSize(bytes) {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  /** Re-checks a row the customer is fixing, once it has been flagged. @param {HTMLElement} row */
  #revalidate(row) {
    if (row.dataset.qtyOnly === 'true') {
      this.#checkQty(row);
      return;
    }
    if (!row.querySelector('[aria-invalid="true"]') && !row.querySelector('[data-row-error]')?.textContent) return;
    this.#validateRow(row, this.#isRequired(row), true);
    this.#clearFixSummary();
  }

  /** Clears "Please fix the highlighted designs." once nothing is flagged; other notes stay. */
  #clearFixSummary() {
    const summary = this.querySelector('[data-form-error]');
    if (summary?.textContent === this.#message('msgFix') && !this.querySelector('[aria-invalid="true"]')) {
      this.#setFormError('');
    }
  }

  /** Row 1 is checked even when empty, but only while every row is empty; other empty rows are skipped. */
  #isRequired(/** @type {HTMLElement} */ row) {
    const rows = this.#rows;
    return row === rows[0] && rows.every((r) => this.#readRow(r).empty);
  }

  /**
   * Notes a press on this form's Add to cart (main or sticky bar) until its click arrives.
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!(event.target instanceof Element) || !this.#isAddButton(event.target)) return;
    this.#pressingAdd = true;
    clearTimeout(this.#pressTimer);
    // A press that never becomes a click (dragged off) stops counting after a moment, and the quantity
    // check it put off runs then.
    this.#pressTimer = window.setTimeout(() => {
      this.#pressingAdd = false;
      const row = this.#pressedQtyRow;
      this.#pressedQtyRow = null;
      if (row?.isConnected && !this.#busy) this.#checkQty(row);
    }, 1500);
  };

  /**
   * @param {Element} target
   * @returns {HTMLButtonElement | HTMLInputElement | null} This form's Add to cart (or sticky bar) button.
   */
  #isAddButton(target) {
    const form = this.#form;
    if (!form) return null;
    const sticky = target.closest('sticky-add-to-cart [ref="addToCartButton"]');
    const button = sticky ?? target.closest('button[type="submit"], input[type="submit"]');
    if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return null;
    if (sticky ? !sticky.closest('.shopify-section')?.contains(form) : button.form !== form) return null;
    return button;
  }

  /**
   * Blocks the theme's add-to-cart (and its "added" animation) while rows are invalid, and keeps
   * the theme's own click handling out of the way when they are valid: the uploads below decide
   * when something has actually been added.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const form = this.#form;
    if (!form) return;
    // Horizon's sticky bar button is type="button": it clicks the main button, then plays its own
    // "added" animation unconditionally, so it has to be stopped here too.
    const sticky = event.target.closest('sticky-add-to-cart [ref="addToCartButton"]');
    const button = sticky ?? event.target.closest('button[type="submit"], input[type="submit"]');
    if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return;
    if (sticky ? !sticky.closest('.shopify-section')?.contains(form) : button.form !== form) return;

    this.#pressingAdd = false;
    this.#pressedQtyRow = null;
    clearTimeout(this.#pressTimer);
    event.stopImmediatePropagation();
    if (this.#busy || !this.#validateAll(true)) {
      event.preventDefault();
      return;
    }

    if (sticky) {
      event.preventDefault();
      // #addAllToCart shows the unavailable message when the main button is disabled.
      this.#addAllToCart(form);
      return;
    }
    // The theme's quantity box is hidden; if it is ever invalid the browser blocks the submit silently.
    const qty = form.querySelector('input[name="quantity"]');
    if (qty instanceof HTMLInputElement && !qty.validity.valid) qty.value = String(this.#min);
  };

  /**
   * Replaces the product form's single-item add with one request per row.
   * @param {SubmitEvent} event
   */
  #onDocumentSubmit = (event) => {
    const form = this.#form;
    if (!form || event.target !== form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#busy) return;
    if (!this.#validateAll(true)) return;
    this.#addAllToCart(form);
  };

  /**
   * @param {boolean} [focus=true]
   * @returns {HTMLElement | null} The new row.
   */
  #addRow(focus = true) {
    const max = parseInt(this.dataset.maxRows || '50', 10);
    if (this.#rows.length >= max) return null;

    const template = this.querySelector('template[data-row-template]');
    const container = this.querySelector('[data-rows]');
    if (!(template instanceof HTMLTemplateElement) || !container) return null;

    this.#rowCounter += 1;
    const html = template.innerHTML.replaceAll('__N__', String(this.#rowCounter));
    const fragment = document.createRange().createContextualFragment(html);
    const row = fragment.querySelector('[data-row]');
    container.append(fragment);
    this.#refreshRows();

    if (focus) {
      const firstInput = row?.querySelector('input');
      if (firstInput instanceof HTMLInputElement) firstInput.focus();
    }
    return row instanceof HTMLElement ? row : null;
  }

  /** @param {HTMLElement} row */
  #removeRow(row) {
    const rows = this.#rows;
    const index = rows.indexOf(row);
    if (index <= 0) return;

    row.remove();
    this.#refreshRows();
    this.#clearFixSummary();

    // Keep focus in the table: the row that moved into this position, or the add button.
    const next = this.#rows[index] ?? this.#rows[index - 1];
    const target = next?.querySelector('input:not([type="file"])') ?? this.querySelector('[data-add]');
    if (target instanceof HTMLElement) target.focus();
  }

  /** Renumbers remove labels, sets quantity placeholders and the add button state. */
  #refreshRows() {
    const rows = this.#rows;
    const placeholder = this.#message('msgQtyPlaceholder', { min: this.#min });

    rows.forEach((row, index) => {
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', this.#message('msgRowLabel', { n: index + 1 }) || `Design ${index + 1}`);
      const remove = row.querySelector('[data-remove]');
      if (remove instanceof HTMLElement) {
        const base = (remove.getAttribute('aria-label') || '').replace(/\s*(__NUM__|\d+)$/, '');
        remove.setAttribute('aria-label', `${base} ${index + 1}`);
        remove.hidden = index === 0;
      }
      const qty = row.querySelector('[data-field="qty"]');
      if (qty instanceof HTMLInputElement) qty.placeholder = placeholder;
    });

    const add = this.querySelector('[data-add]');
    if (add instanceof HTMLButtonElement) {
      add.disabled = this.#busy || rows.length >= parseInt(this.dataset.maxRows || '50', 10);
    }
  }

  /**
   * @param {HTMLElement} row
   * @returns {RowData}
   */
  #readRow(row) {
    const value = (/** @type {string} */ field) => {
      const input = row.querySelector(`[data-field="${field}"]`);
      return input instanceof HTMLInputElement ? input.value.trim() : '';
    };
    const name = value('name');
    const linkMode = this.#isLinkMode(row);
    const link = linkMode ? value('link') : '';
    const qtyText = value('qty');
    const file = this.#files.get(row) ?? null;
    return {
      name,
      file,
      link,
      linkMode,
      qty: parseInt(qtyText, 10) || 0,
      empty: !name && !file && !linkMode && !qtyText,
    };
  }

  /**
   * @param {HTMLElement} row
   * @param {boolean} isFirst - The first row is always checked, even when empty.
   * @param {boolean} show - Whether to show the messages.
   * @returns {boolean}
   */
  #validateRow(row, isFirst, show) {
    const data = this.#readRow(row);
    /** @type {Object} */
    const problems = {};

    if (!(data.empty && !isFirst)) {
      if (!data.name) problems.name = this.#message('msgName');
      if (data.linkMode) {
        if (!LINK_PATTERN.test(data.link)) problems.link = this.#message('msgLink');
      } else if (!data.file) {
        problems.file = this.#message('msgFile');
      }
      if (data.qty < this.#min) problems.qty = this.#message('msgQty', { min: this.#min });
    }

    if (show) {
      delete row.dataset.qtyOnly;
      for (const field of ['name', 'file', 'link', 'qty']) {
        const input = row.querySelector(`[data-field="${field}"]`);
        if (input instanceof HTMLInputElement) input.setAttribute('aria-invalid', String(Boolean(problems[field])));
      }
      row.querySelector('[data-file-button]')?.classList.toggle('is-invalid', Boolean(problems.file));
      const error = row.querySelector('[data-row-error]');
      if (error) error.textContent = Object.values(problems).join(' ');
    }

    return Object.keys(problems).length === 0;
  }

  /**
   * @param {boolean} show
   * @returns {boolean}
   */
  #validateAll(show) {
    const rows = this.#rows;
    let firstInvalid = null;
    const allEmpty = rows.every((row) => this.#readRow(row).empty);

    rows.forEach((row, index) => {
      const valid = this.#validateRow(row, allEmpty && index === 0, show);
      if (!valid && !firstInvalid) firstInvalid = row;
    });

    if (show) {
      this.#setFormError(firstInvalid ? this.#message('msgFix') : '');
      if (firstInvalid) this.#focusFirstProblem(/** @type {HTMLElement} */ (firstInvalid));
    }
    return !firstInvalid;
  }

  /** @param {HTMLElement} row */
  #focusFirstProblem(row) {
    const input = row.querySelector('[aria-invalid="true"]');
    if (input instanceof HTMLElement) {
      input.focus();
      // The file input is visually hidden; bring its button into view instead.
      if (input.dataset.field === 'file') row.querySelector('[data-file-button]')?.scrollIntoView({ block: 'nearest' });
    }
  }

  /** @param {string} text */
  #setFormError(text) {
    const node = this.querySelector('[data-form-error]');
    if (node) node.textContent = text;
  }

  /** @param {string} text */
  #setStatus(text) {
    const node = this.querySelector('[data-status]');
    if (node) node.textContent = text;
  }

  /** @param {number | null} percent - null hides the bar. */
  #setProgress(percent) {
    const bar = this.querySelector('[data-progress]');
    if (!(bar instanceof HTMLProgressElement)) return;
    bar.hidden = percent === null;
    if (percent !== null) bar.value = percent;
  }

  /** Locks the table while uploads run. @param {boolean} busy */
  #setBusy(busy) {
    if (!busy) window.removeEventListener('beforeunload', this.#leaveGuard);
    this.#busy = busy;
    // aria-busy only on the rows: on the host it would also cover the status and alert live regions,
    // which assistive technology may then hold back until the uploads finish.
    const rowsNode = this.querySelector('[data-rows]');
    if (busy) rowsNode?.setAttribute('aria-busy', 'true');
    else rowsNode?.removeAttribute('aria-busy');
    this.classList.toggle('is-busy', busy);
    for (const control of this.querySelectorAll('[data-rows] input, [data-rows] button')) {
      if (control instanceof HTMLInputElement || control instanceof HTMLButtonElement) control.disabled = busy;
    }
    this.#refreshRows();
  }

  #resetRows() {
    const [first, ...rest] = this.#rows;
    for (const row of rest) row.remove();
    if (first) {
      for (const input of first.querySelectorAll('input')) {
        input.value = '';
        input.removeAttribute('aria-invalid');
      }
      this.#files.delete(first);
      delete first.dataset.qtyOnly;
      delete first.dataset.autoName;
      this.#setLinkMode(first, false);
      this.#renderFile(first);
      first.querySelector('[data-file-button]')?.classList.remove('is-invalid');
      const error = first.querySelector('[data-row-error]');
      if (error) error.textContent = '';
    }
    this.#refreshRows();
  }

  /**
   * Sends one design to /cart/add.js, reporting upload progress (fetch cannot).
   * @param {string} url
   * @param {FormData} body
   * @param {AbortSignal} signal
   * @param {(fraction: number) => void} onProgress
   * @returns {Promise<{status: number, body: any}>}
   */
  #post(url, body, signal, onProgress) {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      // A connection that stops moving while the file is still going out would keep the table locked until
      // the OS gives up. Only the sending phase is timed: once the body is out the store may already be
      // adding the line, and failing the row then would invite a duplicate on retry.
      const STALL_MS = 45000;
      let lastProgress = Date.now();
      let sending = true;
      let stalled = false;
      const watchdog = setInterval(() => {
        if (sending && Date.now() - lastProgress > STALL_MS) {
          stalled = true;
          xhr.abort();
        }
      }, 2000);
      xhr.addEventListener('loadend', () => clearInterval(watchdog));
      xhr.upload.addEventListener('load', () => {
        sending = false;
      });
      xhr.upload.addEventListener('progress', (event) => {
        lastProgress = Date.now();
        if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
      });
      xhr.addEventListener('load', () => {
        let parsed = null;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = null;
        }
        resolve({ status: xhr.status, body: parsed });
      });
      xhr.addEventListener('error', () => reject(new TypeError('Network error')));
      xhr.addEventListener('abort', () =>
        reject(stalled ? new TypeError('Upload stalled') : new DOMException('Aborted', 'AbortError'))
      );
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(body);
    });
  }

  /**
   * Whether the cart line really holds an uploaded file. Only a line found without one counts as missing;
   * if the cart cannot be read, the line is kept (the request email flags a missing file either way).
   * @param {string} cartUrl
   * @param {string} key
   * @param {string} fileKey
   * @param {AbortSignal} signal
   */
  async #cartLineHasFile(cartUrl, key, fileKey, signal) {
    try {
      const response = await this.#fetchBounded(cartUrl, { headers: { Accept: 'application/json' } }, signal);
      const cart = response.ok ? await response.json() : null;
      const item = cart?.items?.find((/** @type {any} */ line) => String(line.key) === key);
      return !item || Boolean(item.properties?.[fileKey]);
    } catch (error) {
      if (signal.aborted) throw error;
      return true;
    }
  }

  /**
   * fetch() that gives up after `ms`, so a hung cart request cannot keep the table locked.
   * @param {string} url
   * @param {RequestInit} init
   * @param {AbortSignal} signal - The run's signal; aborting it aborts this request too.
   * @param {number} [ms]
   */
  #fetchBounded(url, init, signal, ms = 15000) {
    const controller = new AbortController();
    const stop = () => controller.abort();
    const timer = setTimeout(stop, ms);
    if (signal.aborted) stop();
    else signal.addEventListener('abort', stop, { once: true });
    return fetch(url, { ...init, signal: controller.signal }).finally(() => {
      clearTimeout(timer);
      signal.removeEventListener('abort', stop);
    });
  }

  /** @param {HTMLFormElement} form */
  async #addAllToCart(form) {
    // Horizon updates input[name="id"] only after the variant's section fetch settles.
    let waited = false;
    if (this.#pendingSelect) this.#setBusy(true);
    while (this.#pendingSelect) {
      waited = true;
      const pending = this.#pendingSelect;
      await pending.catch(() => {});
      if (this.#pendingSelect === pending) this.#pendingSelect = null;
    }
    if (waited) this.#setBusy(false);
    if (this.#variantStale) {
      this.#setFormError(this.#message('msgVariantFailed') || this.#message('msgFailed'));
      return;
    }
    if (waited) {
      // The new variant may have a different minimum (Horizon updates the quantity input's min).
      this.#refreshRows();
      if (!this.#validateAll(true)) return;
    }
    const variantInput = form.querySelector('input[name="id"]');
    const variantId = variantInput instanceof HTMLInputElement && variantInput.value ? Number(variantInput.value) : NaN;
    const mainButton = form.querySelector('button[type="submit"]');
    // Horizon empties the id (or disables the button) when the chosen options have no buyable variant.
    if (!(variantId > 0) || (mainButton instanceof HTMLButtonElement && mainButton.disabled)) {
      this.#setFormError(this.#message('msgUnavailable') || this.#message('msgFailed'));
      return;
    }

    const nameKey = this.dataset.nameKey || 'Design name';
    const fileKey = this.dataset.fileKey || 'Artwork file';
    // Shopify renames uploads to a random name, so the customer's own file name rides along hidden.
    const fileNameKey = this.dataset.fileNameKey || '_Original file name';
    const zippedKey = this.dataset.zippedKey || '_Zipped';
    const linkKey = this.dataset.linkKey || 'Artwork link';
    const jobs = this.#rows.map((row) => ({ row, data: this.#readRow(row) })).filter((job) => !job.data.empty);
    if (jobs.length === 0) return;

    const productForm = form.closest('product-form-component');
    const eventTarget = productForm instanceof HTMLElement ? productForm : this;
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : ''))
      .filter(Boolean);

    this.#setBusy(true);
    this.#setFormError('');
    window.addEventListener('beforeunload', this.#leaveGuard);
    this.#requestController?.abort();
    this.#requestController = new AbortController();
    const { signal } = this.#requestController;

    // @ts-ignore Theme is a global defined in snippets/scripts.liquid
    const routes = window.Theme?.routes ?? {};
    const addUrl = `${String(routes.cart_add_url || '/cart/add').replace(/\.js$/, '')}.js`;
    const cartUrl = `${routes.cart_url || '/cart'}.js`;

    /** @type {Array<{row: HTMLElement, data: RowData, key: string}>} */
    const added = [];
    let addedQuantity = 0;
    /** @type {any} */
    let sections;
    /** @type {{row: HTMLElement, message: string, tooBig: boolean, unread?: boolean} | null} */
    let failure = null;

    try {
      for (const [index, job] of jobs.entries()) {
        const label = { current: index + 1, total: jobs.length, name: job.data.name };
        this.#setStatus(this.#message(job.data.file ? 'msgUploading' : 'msgAdding', label));
        this.#setProgress(job.data.file ? 0 : null);
        if (index === 0) {
          // Started from the sticky bar, the table (and this progress) can be a screen or two away.
          const status = this.querySelector('[data-status]');
          const box = status?.getBoundingClientRect();
          if (status && box && (box.top < 0 || box.bottom > window.innerHeight)) status.scrollIntoView({ block: 'center' });
        }

        const body = new FormData();
        body.append('id', String(variantId));
        body.append('quantity', String(job.data.qty));
        body.append(`properties[${nameKey}]`, job.data.name);
        if (job.data.file) {
          let upload;
          try {
            upload = await this.#uploadBody(job.data.file);
          } catch {
            // The file moved or was deleted after it was picked.
            failure = { row: job.row, message: this.#message('msgRead', { file: job.data.file.name }), tooBig: false, unread: true };
            this.#files.delete(job.row);
            this.#renderFile(job.row);
            break;
          }
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          body.append(`properties[${fileKey}]`, upload.blob, upload.name);
          body.append(`properties[${fileNameKey}]`, job.data.file.name);
          if (upload.zipped) body.append(`properties[${zippedKey}]`, 'yes');
        } else {
          body.append(`properties[${linkKey}]`, job.data.link);
        }
        if (sectionIds.length) {
          body.append('sections', sectionIds.join(','));
          body.append('sections_url', window.location.pathname);
        }

        let response;
        try {
          response = await this.#post(addUrl, body, signal, (fraction) => this.#setProgress(Math.round(fraction * 100)));
        } catch (error) {
          if (/** @type {Error} */ (error)?.name === 'AbortError') throw error;
          failure = { row: job.row, message: this.#message('msgFailed'), tooBig: false };
          break;
        }

        const result = response.body;
        if (response.status === 413 && job.data.file) {
          failure = { row: job.row, message: '', tooBig: true };
          break;
        }
        // Only Shopify's line item JSON proves the add; an HTML page (proxy, bot check, password page) does not.
        if (response.status < 200 || response.status >= 300 || !result || result.status || !result.key) {
          failure = {
            row: job.row,
            message: result?.description || result?.message || this.#message('msgFailed'),
            tooBig: false,
          };
          break;
        }

        if (job.data.file && !result.properties?.[fileKey] && !(await this.#cartLineHasFile(cartUrl, String(result.key), fileKey, signal))) {
          // Shopify added the line but kept no file: take it out again and keep the row to retry.
          const changeUrl = `${String(routes.cart_change_url || '/cart/change').replace(/\.js$/, '')}.js`;
          const removed = await this.#fetchBounded(
            changeUrl,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: String(result.key), quantity: 0 }),
            },
            signal
          ).then(
            (r) => r.ok,
            () => false
          );
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          if (removed) {
            failure = {
              row: job.row,
              message: this.#message('msgNoFile', { file: job.data.file.name }) || this.#message('msgFailed'),
              tooBig: false,
            };
            break;
          }
          // Could not take it out: keep it as added (the request email marks the file as missing),
          // rather than have a retry add a second line.
        }
        added.push({ ...job, key: String(result.key) });
        addedQuantity += job.data.qty;
        if (result.sections) sections = result.sections;
      }
    } catch (error) {
      // Aborted because the table left the page; nothing more to show.
      if (/** @type {Error} */ (error)?.name === 'AbortError') {
        this.#setBusy(false);
        this.#setProgress(null);
        return;
      }
      const next = jobs[added.length];
      failure = next ? { row: next.row, message: this.#message('msgFailed'), tooBig: false } : null;
    }

    this.#setProgress(null);

    if (added.length) {
      // Still locked: a second Add here would upload every row again.
      // Bounded: a hung cart request must not keep the table locked.
      const cart = await this.#fetchBounded(cartUrl, { headers: { Accept: 'application/json' } }, signal)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      if (signal.aborted) {
        this.#setBusy(false);
        return;
      }

      // A full success opens the cart drawer ('add'); a partial one only refreshes it ('update'),
      // so the customer stays on the row that still needs fixing.
      // Without the cart, cart-icon.js would show this run's quantity as the whole cart's count.
      const shownCount = /** @type {any} */ (document.querySelector('cart-icon'))?.currentCartCount;
      const itemCount = Number.isFinite(cart?.item_count)
        ? cart.item_count
        : (Number.isFinite(shownCount) ? shownCount : 0) + addedQuantity;
      let cartSummary = null;
      try {
        cartSummary = cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null;
      } catch {
        cartSummary = null;
      }

      const complete = !failure;
      const deferred = CartLinesUpdateEvent.createPromise();
      eventTarget.dispatchEvent(
        new CartLinesUpdateEvent({
          action: complete ? 'add' : 'update',
          context: 'product',
          lines: complete
            ? added.map((job) => ({ merchandiseId: String(variantId), quantity: job.data.qty }))
            : added.map((job) => ({ id: job.key, quantity: job.data.qty })),
          promise: deferred.promise,
        })
      );
      deferred.resolve(
        /** @type {any} */ ({
          cart: cartSummary,
          detail: {
            items: cart?.items,
            source: 'hc-design-table',
            sections,
            itemCount,
            productId: productForm instanceof HTMLElement ? productForm.dataset.productId : undefined,
            didError: false,
          },
        })
      );
    }

    this.#setBusy(false);

    if (!failure) {
      // The theme's own "added" tick on the button, now that everything is really in the cart.
      const button = /** @type {any} */ (form.querySelector('add-to-cart-component'));
      button?.animateAddToCart?.();
      this.#resetRows();
      this.#setStatus(
        added.length === 1 ? this.#message('msgAddedOne') : this.#message('msgAdded', { count: added.length })
      );
      return;
    }

    // Rows that made it into the cart leave the table; the failed row and any after it stay.
    for (const job of added) job.row.remove();
    this.#refreshRows();

    const { row, message, tooBig, unread } = failure;
    if (tooBig) {
      const file = this.#files.get(row);
      this.#files.delete(row);
      this.#setLinkMode(row, true, file?.name ?? '', 'msgServerTooBig');
      this.#renderFile(row);
    }
    const error = row.querySelector('[data-row-error]');
    if (error && !tooBig) error.textContent = message;

    this.#setStatus('');
    this.#setFormError(
      added.length
        ? this.#message('msgPartial', { added: added.length, total: jobs.length })
        : tooBig
          ? this.#message('msgFix')
          : message
    );
    if (!added.length && !tooBig) {
      eventTarget.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
    }
    const focusTarget = row.querySelector(
      tooBig ? '[data-field="link"]' : unread ? '[data-field="file"]' : 'input:not([type="file"])'
    );
    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  }
}

if (!customElements.get('hc-design-table')) {
  customElements.define('hc-design-table', HcDesignTable);
}
